import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TestPaymentModal = ({ isOpen, onClose, processor }) => {
  const [testAmount, setTestAmount] = useState('10.00');
  const [testCurrency, setTestCurrency] = useState('USD');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  if (!isOpen) return null;

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate test result
      setTestResult({
        success: true,
        transactionId: `test_${Date.now()}`,
        amount: parseFloat(testAmount),
        currency: testCurrency,
        status: 'succeeded',
        message: 'Test payment completed successfully'
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Test payment failed. Please check your configuration.'
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name={processor?.icon} size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                Test {processor?.name} Payment
              </h3>
              <p className="text-sm text-muted-foreground">Verify your payment integration</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-smooth"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Test Amount */}
          <div className="space-y-4">
            <Input
              label="Test Amount"
              type="number"
              value={testAmount}
              onChange={(e) => setTestAmount(e?.target?.value)}
              placeholder="10.00"
              step="0.01"
              min="0"
            />

            <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-800 dark:text-amber-200">
                  <p className="font-medium mb-1">Test Mode Active</p>
                  <p>
                    This will create a test transaction using sandbox credentials.
                    No real money will be charged.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Test Cards Info */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Test Card Numbers:</p>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p>✓ Success: 4242 4242 4242 4242</p>
              <p>✓ Declined: 4000 0000 0000 0002</p>
              <p>✓ Insufficient Funds: 4000 0000 0000 9995</p>
              <p className="text-xs text-muted-foreground mt-2">
                Use any future expiry date and any 3-digit CVC
              </p>
            </div>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`
              p-4 rounded-lg border
              ${testResult?.success 
                ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' :'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
              }
            `}>
              <div className="flex items-start gap-2">
                <Icon 
                  name={testResult?.success ? "CheckCircle2" : "XCircle"} 
                  size={20} 
                  className={testResult?.success ? "text-green-600" : "text-red-600"}
                />
                <div className="flex-1">
                  <p className={`text-sm font-medium mb-1 ${
                    testResult?.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
                  }`}>
                    {testResult?.success ? 'Test Successful!' : 'Test Failed'}
                  </p>
                  <p className={`text-xs ${
                    testResult?.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                  }`}>
                    {testResult?.message}
                  </p>
                  {testResult?.success && (
                    <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-800 space-y-1">
                      <p className="text-xs text-green-700 dark:text-green-300">
                        Transaction ID: {testResult?.transactionId}
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-300">
                        Amount: {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: testResult?.currency
                        })?.format(testResult?.amount)}
                      </p>
                      <p className="text-xs text-green-700 dark:text-green-300">
                        Status: {testResult?.status}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-muted/30 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
          <Button
            iconName={testing ? "Loader2" : "Play"}
            iconClassName={testing ? "animate-spin" : ""}
            onClick={handleTest}
            disabled={testing || !testAmount}
          >
            {testing ? 'Testing...' : 'Run Test Payment'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TestPaymentModal;